import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LINKS } from '../constants/links';
import { useSheetSwipeDismiss } from '../hooks/useSheetSwipeDismiss';
import { suppressGhostClick } from '../utils/dismissFocus';
import { markRead, openNoticeLink, resolveNoticeImageUrl } from '../utils/notifications';
import './styles/SeasonalIntro.css';

const MOBILE_MQ = '(max-width: 767px)';

function isDialogNotice(n) {
  return n && !n.read && (n.type === 'meeting' || n.type === 'parent_meeting' || n.type === 'announcement');
}

function dialogCopy(notice) {
  if (notice.type === 'announcement') {
    return {
      title: notice.title || 'Объявление',
      body: notice.body || 'Пожалуйста, ознакомьтесь с сообщением.',
      icon: 'bi-megaphone-fill',
      theme: 'announce',
      eyebrow: 'Объявление',
    };
  }
  if (notice.type === 'parent_meeting') {
    return {
      title: notice.title || 'Родительское собрание',
      body: notice.body || 'Пожалуйста, ознакомьтесь с сообщением.',
      icon: 'bi-people-fill',
      theme: 'meeting',
      eyebrow: 'Собрание',
    };
  }
  return {
    title: notice.title || 'Совещание',
    body: notice.body || 'Пожалуйста, ознакомьтесь с сообщением.',
    icon: 'bi-person-workspace',
    theme: 'meeting',
    eyebrow: 'Совещание',
  };
}

/**
 * Диалог совещания / собрания / объявления — сразу после входа и во время работы.
 * В document.body, чтобы быть поверх dock и остального UI.
 */
const MeetingNoticeIntro = ({ notifications, setNotifications }) => {
  const [notice, setNotice] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false
  );
  const shownRef = useRef(null);
  const sheetRef = useRef(null);
  const handleRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    if (leaving) return undefined;
    if (notice) {
      const fresh = (notifications || []).find((n) => String(n.id) === String(notice.id));
      if (fresh && fresh.read) {
        shownRef.current = String(notice.id);
        setNotice(null);
      }
      return undefined;
    }
    const next = (notifications || []).find(isDialogNotice);
    if (!next) return undefined;
    if (shownRef.current === String(next.id)) return undefined;
    const t = window.setTimeout(() => setNotice(next), 180);
    return () => window.clearTimeout(t);
  }, [notifications, notice, leaving]);

  useEffect(() => {
    if (!notice) return undefined;
    document.body.classList.add('kkt-dialog-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('kkt-dialog-open');
      document.body.style.overflow = prev;
    };
  }, [notice]);

  const dismiss = useCallback(() => {
    if (leaving || !notice) return;
    suppressGhostClick(420);
    setLeaving(true);
    shownRef.current = String(notice.id);
    const next = markRead(notifications || [], notice.id);
    setNotifications?.(next);
    window.setTimeout(() => {
      setNotice(null);
      setLeaving(false);
    }, 320);
  }, [leaving, notice, notifications, setNotifications]);

  const swipeDismiss = useCallback(() => {
    if (leaving || !notice) return;
    suppressGhostClick(420);
    shownRef.current = String(notice.id);
    const next = markRead(notifications || [], notice.id);
    setNotifications?.(next);
    setLeaving(true);
    window.setTimeout(() => {
      setNotice(null);
      setLeaving(false);
    }, 280);
  }, [leaving, notice, notifications, setNotifications]);

  useSheetSwipeDismiss({
    sheetRef,
    handleRef,
    backdropRef,
    onDismiss: swipeDismiss,
    enabled: Boolean(notice) && !leaving && isMobile,
  });

  const openLink = () => {
    if (leaving || !notice) return;
    openNoticeLink(notice.link, LINKS.eo);
    dismiss();
  };

  if (!notice || typeof document === 'undefined') return null;

  const copy = dialogCopy(notice);
  const imageUrl = resolveNoticeImageUrl(notice.image, LINKS.eo);
  const link = (notice.link || '').trim();
  const hasLink = Boolean(link) && link !== '#';

  return createPortal(
    <div
      className={`seasonal-intro seasonal-intro--notice theme-${copy.theme}${leaving ? ' is-leaving' : ''}${imageUrl ? ' has-photo' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      onClick={dismiss}
    >
      <div ref={backdropRef} className="seasonal-intro__veil" aria-hidden="true" />
      <div
        ref={sheetRef}
        className="seasonal-intro__card"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={handleRef} className="seasonal-intro__handle" aria-hidden="true">
          <span className="seasonal-intro__grabber" />
        </div>
        <div className="seasonal-intro__burst" aria-hidden="true" />
        <div className="seasonal-intro__top">
          <div className="seasonal-intro__icon" aria-hidden="true">
            <i className={`bi ${copy.icon}`} />
          </div>
          <h2 className="seasonal-intro__title">{copy.title}</h2>
        </div>
        {imageUrl ? (
          <img
            className="seasonal-intro__photo"
            src={imageUrl}
            alt=""
            decoding="async"
            referrerPolicy="no-referrer"
          />
        ) : null}
        <p className="seasonal-intro__text">{copy.body}</p>
        <div className={`seasonal-intro__actions${hasLink ? '' : ' seasonal-intro__actions--one'}`}>
          {hasLink ? (
            <button type="button" className="seasonal-intro__btn" onClick={openLink}>
              Открыть
            </button>
          ) : null}
          <button
            type="button"
            className={`seasonal-intro__btn${hasLink ? ' seasonal-intro__btn--ghost' : ''}`}
            onClick={dismiss}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MeetingNoticeIntro;
